import { CloseButton, Flex, Text, useDisclosure } from "@chakra-ui/react";
import React from "react";
import { Debug } from "src/components/Debug";
import { SwitchInput } from "src/components/Inputs/SwitchInput";
import { DebugMessage, DebugMessageEvent } from "./CustomEvents";
import { DocumentViewerElement } from "./DocumentViewerElement";

type Props = {
  documentViewerRef: React.RefObject<DocumentViewerElement | undefined>;
};
export const DocumentViewerDebugger = ({ documentViewerRef }: Props) => {
  const { onToggle, isOpen, onClose } = useDisclosure();

  const [showCanvasDebugger, setShowCanvasDebugger] = React.useState(false);
  const [showDocumentDebugger, setShowDocumentDebugger] = React.useState(false);
  const [showContainerDebugger, setShowContainerDebugger] =
    React.useState(false);
  const [showHighResolutionPdfDebugger, setShowHighResolutionPdfDebugger] =
    React.useState(false);

  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });

  const toggleDebugger = React.useCallback(
    (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.key === "d"
      ) {
        event.preventDefault();
        onToggle();
      }
    },
    [onToggle]
  );

  React.useEffect(() => {
    window.document.addEventListener("keydown", toggleDebugger);
    console.info(
      "Document viewer: press cmd/ctrl+shift+D to open debugger panel"
    );
    return () => {
      window.document.removeEventListener("keydown", toggleDebugger);
    };
  }, [toggleDebugger]);

  const [debugData, setDebugData] = React.useState<{ [label: string]: any }>(
    {}
  );
  const debugHandler = React.useCallback((event: DebugMessageEvent) => {
    setDebugData((debug) => ({
      ...debug,
      [event.detail.label]: event.detail.data,
    }));
  }, []);
  React.useEffect(() => {
    const documentViewer = documentViewerRef.current;
    if (!documentViewer) {
      return;
    }

    documentViewer.addEventListener(DebugMessage, debugHandler);

    return () => {
      if (!documentViewer) {
        return;
      }

      documentViewer.removeEventListener(DebugMessage, debugHandler);
    };
  }, [debugHandler, documentViewerRef]);

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    },
    [position]
  );

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    },
    [isDragging, dragOffset]
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  if (!isOpen) {
    return null;
  }
  return (
    <Flex
      bg="gray.50"
      shadow="md"
      left={`${position.x}px`}
      top={`${position.y}px`}
      flexDirection="column"
      position="absolute"
      style={{ cursor: isDragging ? "grabbing" : "default" }}
    >
      <Flex
        justifyContent="space-between"
        onMouseDown={handleMouseDown}
        cursor={isDragging ? "grabbing" : "grab"}
        userSelect="none"
        bg="gray.600"
        alignItems="center"
        color="white"
        paddingLeft="2"
        border="2px solid white"
      >
        <Text fontSize="sm" fontWeight="bold">
          Debugger
        </Text>
        <CloseButton onClick={onClose} size="sm" justifySelf="end" />
      </Flex>
      <Flex direction="column" padding="2">
        <Flex direction="column">
          <SwitchInput
            formLabelProps={{ margin: 0 }}
            labelDirection="row"
            label="Canvas"
            checked={[true, ""]}
            onChange={(value) => {
              setShowCanvasDebugger(value ?? false);
              documentViewerRef.current?.setDebugger("canvas", value ?? false);
            }}
            value={showCanvasDebugger}
          />
          <SwitchInput
            formLabelProps={{ margin: 0 }}
            labelDirection="row"
            label="Document"
            checked={[true, ""]}
            onChange={(value) => {
              setShowDocumentDebugger(value ?? false);
              documentViewerRef.current?.setDebugger(
                "document",
                value ?? false
              );
            }}
            value={showDocumentDebugger}
          />
          <SwitchInput
            formLabelProps={{ margin: 0 }}
            labelDirection="row"
            label="Container"
            checked={[true, ""]}
            onChange={(value) => {
              setShowContainerDebugger(value ?? false);
              documentViewerRef.current?.setDebugger(
                "container",
                value ?? false
              );
            }}
            value={showContainerDebugger}
          />
          <SwitchInput
            formLabelProps={{ margin: 0 }}
            labelDirection="row"
            label="High resolution PDF"
            checked={[true, ""]}
            onChange={(value) => {
              setShowHighResolutionPdfDebugger(value ?? false);
              documentViewerRef.current?.setDebugger(
                "highResolutionPdf",
                value ?? false
              );
            }}
            value={showHighResolutionPdfDebugger}
          />
        </Flex>
        <Flex>
          <Debug
            open
            values={Object.keys(debugData).map((key) => [key, debugData[key]])}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
